import React from "react";
import "./DigitTicker.css";

const DEFAULT_GLYPHS = "0123456789";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

type DecodeTextProps = {
  text: string;
  /** ms between scramble frames. */
  frameMs?: number;
  /** ms before each character locks into its final value. */
  perCharMs?: number;
  /** Decode only the first time it enters view, then never again. */
  once?: boolean;
  /** Scramble alphabet. Themes pass their own so the effect reads native. */
  glyphs?: string;
  className?: string;
};

/**
 * Renders text that scrambles through random digits and resolves left-to-right
 * into the real text whenever it scrolls into view (the "decode" effect). Uses a
 * fixed-width mono font only while animating, then settles back into the
 * surrounding font so titles keep their normal look. Honors
 * prefers-reduced-motion (renders static).
 */
const DecodeText: React.FC<DecodeTextProps> = ({
  text,
  frameMs = 45,
  perCharMs = 55,
  once = false,
  glyphs = DEFAULT_GLYPHS,
  className = "",
}) => {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = React.useState(text);
  const running = React.useRef(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;

    const chars = text.split("");
    const eligible = chars.map((ch) => /[a-zA-Z0-9]/.test(ch));

    const run = () => {
      if (running.current) return;
      running.current = true;
      const start = performance.now();
      const tick = () => {
        const elapsed = performance.now() - start;
        let allDone = true;
        const out = chars
          .map((ch, i) => {
            if (!eligible[i]) return ch;
            if (elapsed >= i * perCharMs) return ch;
            allDone = false;
            return glyphs[Math.floor(Math.random() * glyphs.length)];
          })
          .join("");
        setDisplay(out);
        if (allDone) {
          setDisplay(text);
          running.current = false;
        } else {
          window.setTimeout(() => requestAnimationFrame(tick), frameMs);
        }
      };
      requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          run();
          if (once) observer.disconnect();
        });
      },
      { threshold: 0.6 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, frameMs, perCharMs, once, glyphs]);

  return (
    <span ref={ref} className={`decode-text ${className}`.trim()} aria-label={text}>
      <span aria-hidden="true">{display}</span>
    </span>
  );
};

export default DecodeText;
